/* eslint-disable */
import { useContext, useEffect, useRef, useState } from 'react'
import cytoscape from 'cytoscape'
import { OrgAppContext } from '../../OrgAppContext'
import { getProviderRole } from '../../utils/provider'
import IsSearchingCircle from './IsSearchingCircleAndInfo'

const NodeTooltip = ({ info, position, type }: { info: any; position: any; type: string }) => {
  const { customerId } = useContext(OrgAppContext)
  const providerRole = getProviderRole(customerId)

  if (!info.hoveredNode) return null

  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '10px',
        minWidth: '220px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
        zIndex: 1000,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {type === 'Provider' ? `${providerRole}: ` : 'Client: '}
          </div>
          <div style={{ fontSize: '14px', marginLeft: '3px' }}>{info.centerNode}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {type === 'Provider' ? 'Client: ' : `${providerRole}: `}
          </div>
          <div style={{ fontSize: '14px', marginLeft: '3px' }}>{info.hoveredNode}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>Access Count: </div>
          <div style={{ fontSize: '14px', marginLeft: '3px' }}>{info.accessCount}</div>
        </div>
      </div>
    </div>
  )
}

export default function AccessChart({ type, nodes, edges, isLoading, info, graphType }: any) {
  const cyContainer = useRef<any>(null)
  const [hoverInfo, setHoverInfo] = useState({
    centerNode: '',
    hoveredNode: '',
    accessCount: 0,
  })
  const [tipPosition, setTipPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (cyContainer.current) {
      const cy = cytoscape({
        container: cyContainer.current,
        elements: nodes.concat(edges),
        style: [
          {
            selector: 'node',
            style: {
              label: 'data(label)',
              'text-valign': 'center',
              'text-halign': 'center',
              'background-color': '#858585',
              color: '#ffffff',
              shape: 'round-rectangle',
              'font-size': '12px',
              width: 'label',
              height: 30,
              'padding-bottom': '3px',
              'padding-left': '3px',
              'padding-right': '3px',
              'padding-top': '3px',
            },
          },
          {
            selector: 'node.mainNode',
            style: {
              'background-color': '#2D2F32',
            },
          },
          {
            selector: 'edge',
            style: {
              width: 3,
              'line-color': '#D4D4D4',
              'curve-style': 'bezier',
              label: 'data(label)',
              'text-background-color': '#fff',
              'text-background-opacity': 1,
              'text-background-padding': '5px',
              'text-background-shape': 'roundrectangle',
              'font-size': '13px',
              color: '#000000',
              'text-rotation': 'autorotate',
            },
          },
        ],
        layout: {
          name: graphType as any,
          fit: true,
          minNodeSpacing: 20,
          spacingFactor: 0.9,
          avoidOverlap: true,
        },
      })

      let isAnimating = false
      const nodeCount = cy.nodes().length
      const scaleFactor = nodeCount > 40 ? 2.5 : nodeCount > 20 ? 1.5 : 1.1

      cy.on('mouseover', 'node', (event) => {
        const node = event.target
        if (node.hasClass('mainNode')) {
          return
        }
        const centerNode = cy.$('.mainNode')

        const connectedEdge = node
          .connectedEdges()
          .filter(
            (edge: any) =>
              edge.source().id() === centerNode.id() || edge.target().id() === centerNode.id()
          )

        const accessCount = connectedEdge.data('label')

        const info = {
          centerNode: centerNode.data('label'),
          hoveredNode: node.data('label'),
          accessCount: accessCount,
        }

        setHoverInfo(info)
        // set tooltip position in order to not cover the node
        const position = node.renderedPosition()
        setTipPosition({
          x: position.x + 100,
          y: position.y - 50,
        })

        if (isAnimating) return

        isAnimating = true

        node.animate({
          style: {
            width: `${150 * scaleFactor}px`,
            height: `${80 * scaleFactor}px`,
            'font-size': `${18 * scaleFactor}px`,
          },
          duration: 100,
          easing: 'ease-out',
          complete: () => {
            isAnimating = false
          },
        })
        node.connectedEdges().forEach((edge: any) => {
          const sourceId = edge.source().id()
          const targetId = edge.target().id()

          if (
            cy.$(`#${sourceId}`).hasClass('mainNode') ||
            cy.$(`#${targetId}`).hasClass('mainNode')
          ) {
            edge.animate({
              style: {
                'font-size': `${20 * scaleFactor}px`,
              },
              duration: 100,
              easing: 'ease-out',
            })
          }
        })

        cy.nodes().forEach((n) => {
          const distance = node.neighborhood().intersection(n).length
          if (distance > 0 && distance === 1) {
            n.animate({
              style: {
                width: `${150 * scaleFactor}px`,
                height: `${80 * scaleFactor}px`,
                'font-size': `${18 * scaleFactor}px`,
              },
              duration: 100,
              easing: 'ease-out',
            })
          }
        })
      })

      cy.on('mouseout', 'node', (event) => {
        setHoverInfo({
          centerNode: '',
          hoveredNode: '',
          accessCount: 0,
        })
        cy.nodes().forEach((n) => {
          n.animate({
            style: {
              width: 'label',
              height: 30,
              'font-size': '12px',
            },
            duration: 50,
            easing: 'ease-out',
          })
        })
        cy.edges().forEach((edge) => {
          edge.animate({
            style: {
              'font-size': '13px',
            },
            duration: 50,
            easing: 'ease-out',
          })
        })
      })
    }
  }, [nodes, edges, graphType])

  return (
    <div style={{ height: 'calc(100% - 42px)' }}>
      <div style={{ height: '100%' }}>
        <div className='graphBodyContainer'>
          <div className='graphBody'>
            {isLoading || info.length > 0 ? (
              <IsSearchingCircle info={info} />
            ) : (
              <div
                ref={cyContainer}
                style={{ width: '100%', height: '100%', position: 'relative' }}
              >
                <NodeTooltip info={hoverInfo} position={tipPosition} type={type} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
